/**
 * O painel esquerdo: a sanfona Região › Área › Cenas.
 *
 * <p>A geografia é o esqueleto, e as cenas penduram nela. Quem escreve o livro-jogo
 * pensa "o que acontece na Mata das Serpentes", e não "quais são as cenas de nível 2" —
 * por isso a árvore começa pelo mapa, e não pelas cenas soltas.
 *
 * <p><b>Tudo nasce fechado.</b> Com sete regiões, sete áreas cada e as cenas dentro
 * delas, a árvore aberta é uma parede de texto onde não se acha nada; fechada, ela é um
 * índice. Buscar reabre o que casa, para o resultado nunca ficar escondido.
 *
 * <p>Abrir uma área também mostra o que ela <b>já tem cadastrado e ainda não virou
 * cena</b> — o local, o NPC, a criatura, o coletável. Sem isso a área recém-escrita no
 * módulo do mundo parece vazia aqui, e ninguém adivinha o que falta.
 */
import { useState } from 'react';

import type { AreaComCenas, CenaNaArvore, PaginaDeCenas, TipoDeCena } from './cenasTipos';
import { COR_DO_TIPO, ROTULO_DO_TIPO } from './cenasTipos';

/**
 * A chave de um nó.
 *
 * <p>É a ficha, e não a cena: o nó existe antes de haver linha em `cena`, e continua
 * sendo o mesmo nó depois que ela nasce. Usar o id da cena faria o React trocar o
 * componente inteiro na primeira vez que alguém escrevesse — perdendo o estado de
 * aberto/fechado justo no clique que abriu o editor.
 */
export const chaveDoNo = (no: CenaNaArvore) => `${no.alvoColecao}-${no.alvoId}`;

interface Props {
  pagina: PaginaDeCenas;
  selecionada: number | null;
  aoAbrirNo: (no: CenaNaArvore, areaId: number) => void;
  aoReordenar: (nivel: CenaNaArvore[], areaId: number) => void;
  aoMover: (id: number, direcao: 'cima' | 'baixo') => void;
  busca: string;
  tiposAtivos: Set<TipoDeCena>;
  aberto: Record<string, boolean>;
  aoAbrir: (chave: string, aberto: boolean) => void;
}

/** O que as linhas precisam saber, num objeto só — são muitos níveis de aninhamento. */
interface Contexto {
  termo: string;
  tiposAtivos: Set<TipoDeCena>;
  selecionada: number | null;
  aoMover: (id: number, direcao: 'cima' | 'baixo') => void;
  estaAberto: (chave: string) => boolean;
  aoAbrir: (chave: string, aberto: boolean) => void;
  /** Abrir o nó para escrever. Sobrescrito por área, que sabe o próprio id. */
  aoAbrirNo: (no: CenaNaArvore) => void;
  /** A nova ordem de um nível, depois de arrastar. Sobrescrito por área. */
  aoReordenar: (nivel: CenaNaArvore[]) => void;
  /**
   * A chave do nó em viagem, ou nada.
   *
   * <p>Precisa ser estado, e não o `dataTransfer`: o navegador só deixa ler o conteúdo
   * dele no `drop`, e é durante o `dragover` que a linha precisa decidir se aceita a
   * solta — antes disso não haveria como recusar um NPC caindo entre locais.
   */
  arrastando: string | null;
  aoArrastar: (chave: string | null) => void;
}

/**
 * Reordena uma lista movendo um item para a posição de outro.
 *
 * <p>Move, e não troca: arrastar o quinto para o primeiro empurra os quatro de cima
 * um degrau para baixo, que é o que a mão espera. Trocar deixaria o primeiro no
 * quinto lugar, e ninguém arrasta esperando isso.
 */
function reordenado<T>(lista: T[], de: number, para: number): T[] {
  const novo = [...lista];
  novo.splice(para, 0, ...novo.splice(de, 1));
  return novo;
}

export function ArvoreDeCenas({
  pagina,
  selecionada,
  aoAbrirNo,
  aoReordenar,
  aoMover,
  busca,
  tiposAtivos,
  aberto,
  aoAbrir,
}: Props) {
  const termo = busca.trim().toLowerCase();
  const [arrastando, setArrastando] = useState<string | null>(null);

  const ctx: Contexto = {
    termo,
    tiposAtivos,
    selecionada,
    aoMover,
    arrastando,
    aoArrastar: setArrastando,
    // Fechado é o padrão; buscando, tudo abre — esconder o resultado atrás de uma
    // sanfona fechada transformaria a busca em adivinhação.
    estaAberto: (chave) => (termo ? true : aberto[chave] === true),
    aoAbrir,
    // Sobrescritos por área: só ela sabe o próprio id, que a criação da cena precisa.
    aoAbrirNo: () => {},
    aoReordenar: () => {},
  };

  const semArea = pagina.semArea.filter((c) => naSubarvore(c, ctx));

  return (
    <div className="cn-arvore">
      {pagina.regioes.map((regiao) => {
        const areasVisiveis = regiao.areas.filter(
          (a) => !termo || a.cenas.filter((c) => naSubarvore(c, ctx)).length > 0
        );
        if (termo && areasVisiveis.length === 0) return null;
        const expandida = ctx.estaAberto(`r${regiao.id}`);

        return (
          <div className="cn-no" key={regiao.id}>
            <div
              className="cn-regiao"
              onClick={() => aoAbrir(`r${regiao.id}`, !expandida)}
              style={regiao.corMapa ? { borderLeftColor: regiao.corMapa } : undefined}
            >
              <span className={`cn-chev${expandida ? ' aberta' : ''}`}>▶</span>
              <span className="cn-regiao-nome">{regiao.nome}</span>
              <span className="cn-conta">{regiao.areas.length} área(s)</span>
            </div>
            {expandida && (
              <div className="cn-filhas">
                {areasVisiveis.map((a) => (
                  <AreaDaArvore
                    key={a.id}
                    area={a}
                    ctx={ctx}
                    aoAbrirNo={aoAbrirNo}
                    aoReordenar={aoReordenar}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* O prólogo: o que acontece antes de o jogador estar em lugar algum. */}
      {semArea.length > 0 && (
        <div className="cn-no">
          <div
            className="cn-regiao"
            onClick={() => aoAbrir('prologo', !ctx.estaAberto('prologo'))}
          >
            <span className={`cn-chev${ctx.estaAberto('prologo') ? ' aberta' : ''}`}>▶</span>
            <span className="cn-regiao-nome">Sem área · prólogo</span>
            <span className="cn-conta">{semArea.length}</span>
          </div>
          {ctx.estaAberto('prologo') && (
            <div className="cn-filhas">
              {semArea.map((c) => (
                <LinhaDeCena key={`solta-${c.id}`} cena={c} irmas={semArea} ctx={ctx} />
              ))}
            </div>
          )}
        </div>
      )}

      {pagina.regioes.length === 0 && semArea.length === 0 && (
        <div className="cn-vazio">
          <b>Nenhuma região no mapa ainda</b>
          As cenas são o próprio mundo: crie uma região e as áreas dela no Mapa da Ilha, e
          elas aparecem aqui para escrever.
        </div>
      )}
    </div>
  );
}

/**
 * Uma área e o que há dentro dela: as cenas já escritas e, abaixo, o conteúdo que
 * ainda não virou cena.
 *
 * <p>Componente de módulo, e não uma função dentro do pai, porque ele tem consulta
 * própria: definido lá dentro, remontaria a cada render e refaria a busca sem parar.
 */
function AreaDaArvore({
  area,
  ctx,
  aoAbrirNo,
  aoReordenar,
}: {
  area: AreaComCenas;
  ctx: Contexto;
  aoAbrirNo: (no: CenaNaArvore, areaId: number) => void;
  aoReordenar: (nivel: CenaNaArvore[], areaId: number) => void;
}) {
  const visiveis = area.cenas.filter((c) => naSubarvore(c, ctx));
  const expandida = ctx.estaAberto(`a${area.id}`);

  /*
   * Não há mais consulta de candidatos aqui, e é a diferença que este PR faz.
   *
   * Antes a árvore vinha da tabela `cena` e o conteúdo sem cena vinha de uma segunda
   * chamada, feita ao abrir a área; a tela costurava as duas, decidindo em qual local
   * pendurar cada candidato. Era na costura que o conteúdo sumia. Agora `area.cenas`
   * já é o mundo inteiro daquela área — escrito e por escrever, cada um no seu lugar.
   */

  const ctxDaArea: Contexto = {
    ...ctx,
    aoAbrirNo: (no) => aoAbrirNo(no, area.id),
    aoReordenar: (nivel) => aoReordenar(nivel, area.id),
  };

  if (ctx.termo && visiveis.length === 0) return null;

  return (
    <div className="cn-no">
      <div className="cn-linha area" onClick={() => ctx.aoAbrir(`a${area.id}`, !expandida)}>
        <span className={`cn-chev${expandida ? ' aberta' : ''}`}>▶</span>
        <span className="cn-badge area-bd">ÁREA</span>
        <span className="cn-nome">{area.nome}</span>
        <span className="cn-conta">{visiveis.length}</span>
      </div>

      {expandida && (
        <div className="cn-filhas">
          {visiveis.map((c) => (
            <LinhaDeCena key={chaveDoNo(c)} cena={c} irmas={visiveis} ctx={ctxDaArea} />
          ))}
        </div>
      )}
    </div>
  );
}

function LinhaDeCena({
  cena,
  irmas,
  ctx,
}: {
  cena: CenaNaArvore;
  irmas: CenaNaArvore[];
  ctx: Contexto;
}) {
  const filhasVisiveis = cena.filhas.filter((f) => naSubarvore(f, ctx));
  const temFilhos = filhasVisiveis.length > 0;
  const chave = chaveDoNo(cena);
  const expandida = ctx.estaAberto(`c${chave}`);
  const onde = irmas.indexOf(cena);
  const [alvoDeSolta, setAlvoDeSolta] = useState<'antes' | 'depois' | null>(null);

  /*
   * Arrastar só reordena entre IRMÃS DO MESMO TIPO.
   *
   * A árvore agrupa por tipo — locais, NPCs, criaturas, coletáveis, eventos —, e essa
   * ordem vem do servidor. Deixar arrastar um NPC para o meio dos locais seria oferecer
   * um movimento que o próximo carregamento desfaz, que é pior do que não oferecer.
   */
  const podeSoltar = (arrastado: CenaNaArvore) =>
    arrastado.tipo === cena.tipo && chaveDoNo(arrastado) !== chave;

  return (
    <div className="cn-no">
      <div
        draggable
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', chave);
          ctx.aoArrastar(chave);
        }}
        onDragOver={(e) => {
          const vindo = irmas.find((i) => chaveDoNo(i) === ctx.arrastando);
          if (!vindo || !podeSoltar(vindo)) return;
          e.preventDefault();
          e.stopPropagation();
          // Metade de cima solta antes; metade de baixo, depois. Sem isso não haveria
          // como pôr algo na última posição da fileira.
          const caixa = e.currentTarget.getBoundingClientRect();
          setAlvoDeSolta(e.clientY < caixa.top + caixa.height / 2 ? 'antes' : 'depois');
        }}
        onDragLeave={() => setAlvoDeSolta(null)}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAlvoDeSolta(null);
          const vindo = irmas.find((i) => chaveDoNo(i) === ctx.arrastando);
          if (!vindo || !podeSoltar(vindo)) return;
          const de = irmas.indexOf(vindo);
          let para = irmas.indexOf(cena) + (alvoDeSolta === 'depois' ? 1 : 0);
          // Tirar o arrastado encurta a lista antes dele: sem este ajuste, arrastar
          // para baixo sempre para uma casa antes do lugar onde se soltou.
          if (de < para) para -= 1;
          if (de !== para) ctx.aoReordenar(reordenado(irmas, de, para));
        }}
        onDragEnd={() => {
          setAlvoDeSolta(null);
          ctx.aoArrastar(null);
        }}
        className={`cn-linha${ctx.selecionada != null && ctx.selecionada === cena.id ? ' sel' : ''}${
          cena.escrita ? '' : ' candidato'
        }${alvoDeSolta ? ` solta-${alvoDeSolta}` : ''}`}
        onClick={() => ctx.aoAbrirNo(cena)}
        title={cena.escrita ? undefined : `Escrever ${cena.nome}`}
      >
        <button
          type="button"
          className={`cn-chev${temFilhos ? '' : ' folha'}${expandida ? ' aberta' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (temFilhos) ctx.aoAbrir(`c${chave}`, !expandida);
          }}
          aria-label={expandida ? 'Recolher' : 'Expandir'}
        >
          ▶
        </button>

        <span
          className="cn-badge"
          style={{ '--c': COR_DO_TIPO[cena.tipo] } as React.CSSProperties}
        >
          {ROTULO_DO_TIPO[cena.tipo]}
        </span>

        <span className="cn-nome">{cena.nome}</span>

        {/* O nó É a ficha, então não há "→ alvo" a mostrar. O que sobra dizer é onde
            ele está, quando o lugar não é óbvio pelo aninhamento. */}
        {cena.local && <span className="cn-lk">em {cena.local}</span>}

        {/* O que distingue a linha por escrever não é o lugar dela, é a falta de texto. */}
        {!cena.escrita && <span className="cn-virar">sem texto</span>}

        {cena.destinos.length > 0 && <span className="cn-op">⤷ {cena.destinos.length}</span>}

        {cena.codigo && <span className="cn-cod">{cena.codigo}</span>}

        {/* Reordenar entre irmãs. Some quando não há para onde ir — seta que não faz
            nada é pior que seta nenhuma. */}
        {/* Reordenar só faz sentido em quem já tem cena: a ordem mora nela. Quem ainda
            não foi escrito se ordena por nome, e não há o que arrastar. */}
        {cena.id != null && (
          <span className="cn-setas">
            {onde > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  ctx.aoMover(cena.id as number, 'cima');
                }}
                title="Subir entre as irmãs"
              >
                ↑
              </button>
            )}
            {onde >= 0 && onde < irmas.length - 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  ctx.aoMover(cena.id as number, 'baixo');
                }}
                title="Descer entre as irmãs"
              >
                ↓
              </button>
            )}
          </span>
        )}
      </div>

      {temFilhos && expandida && (
        <div className="cn-filhas">
          {filhasVisiveis.map((f) => (
            <LinhaDeCena key={chaveDoNo(f)} cena={f} irmas={filhasVisiveis} ctx={ctx} />
          ))}
        </div>
      )}
    </div>
  );
}

/** A cena em si passa pelo filtro? */
function combina(cena: CenaNaArvore, ctx: Contexto): boolean {
  if (!ctx.tiposAtivos.has(cena.tipo)) return false;
  if (!ctx.termo) return true;
  return (
    cena.nome.toLowerCase().includes(ctx.termo) ||
    (cena.codigo ?? '').toLowerCase().includes(ctx.termo) ||
    (cena.alvo ?? '').toLowerCase().includes(ctx.termo)
  );
}

/**
 * Ela ou alguém dentro dela. Um ramo cujo neto casa com a busca precisa continuar
 * visível — senão o resultado apareceria sem o caminho que leva até ele.
 */
function naSubarvore(cena: CenaNaArvore, ctx: Contexto): boolean {
  return combina(cena, ctx) || cena.filhas.some((f) => naSubarvore(f, ctx));
}
